import { useEffect, useState } from "react";
import { Outlet, useParams, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { MiniSiteContext, MINI_SITE_NAV } from "../components/minisite/context";
import MiniSiteNav, { MiniSiteFooter } from "../components/minisite/MiniSiteNav";
import RentalProfile from "./RentalProfile";
import SEO from "@/components/SEO";
import { absUrl } from "@/lib/seo";
import ShareButton from "../components/share/ShareButton";
import DemoBusinessGuard from "../components/DemoBusinessGuard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SimpleTopBar({ slug, name, description }) {
  return (
    <div className="border-b border-slate-200 bg-white" data-testid="ms-simple-topbar">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-11 flex items-center justify-between gap-3">
        <Link to="/rentals" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> All rentals
        </Link>
        <div className="flex items-center gap-2">
          {slug && (
            <ShareButton
              slug={slug}
              businessName={name}
              description={description}
              variant="ghost"
              size="sm"
              testId="ms-simple-share-btn"
              label="Share"
            />
          )}
          <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900">
            <ShieldCheck className="w-3.5 h-3.5" /> Powered by <strong className="text-[#1B4332]">ConfirmPH</strong>
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Top-level wrapper for `/rentals/:slug/*`.
 *
 *  - Loads `/api/public/rentals/:slug` once.
 *  - If business is Enterprise: renders the multi-page mini-site shell
 *    (sticky nav, themed footer) and yields to nested page routes.
 *  - If business is NOT Enterprise: defers to the legacy single-page
 *    `<RentalProfile />` so Free/Basic/Pro users keep the existing UX.
 */
export default function MiniSiteLayout() {
  const { slug } = useParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/public/rentals/${slug}`);
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-20 flex items-center justify-center text-slate-500"
           data-testid="ms-layout-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="ms-layout-unavailable">
        <SEO noindex title="Business unavailable · ConfirmPH" />
        <h2 className="font-display text-2xl font-semibold">This business listing is currently unavailable.</h2>
        <p className="mt-2 text-sm text-slate-600">
          It may have been hidden by the owner or removed. If you booked here, your booking and confirmation links are not affected.
        </p>
        <Link to="/rentals" className="mt-4 inline-flex items-center gap-1 text-[#1B4332] underline text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to Discover Rentals
        </Link>
      </div>
    );
  }

  // Free / Basic / Pro → keep the legacy single-page profile. If they hit a
  // sub-page like /fleet, /faq, etc., bounce them to the slug root.
  // ALSO: Enterprise businesses with `public_website_enabled === false`
  // fall through to the simple profile (their mini-site is deactivated).
  // ALSO: ?profile=1 forces the legacy profile view for Enterprise: used
  // by the "View business" button on the Discover Rentals card so users
  // can choose between the website and the normal profile.
  const websiteOff = data.public_website_enabled === false;
  const forceProfile = new URLSearchParams(location.search).get("profile") === "1";
  if (!data.is_enterprise || websiteOff || forceProfile) {
    const subPaths = MINI_SITE_NAV.map((n) => `/${n.to}`);
    const hitSub = subPaths.some((p) => location.pathname.endsWith(p));
    if (hitSub) return <Navigate to={`/rentals/${slug}`} replace />;
    const simpleSlug = (data.public_slug || slug).toLowerCase();
    const simpleName = data.name || "Rental business";
    const simpleDesc =
      (data.public_description_full || data.description || "").slice(0, 220) ||
      `View ${simpleName}'s public profile, contact details and booking options through ConfirmPH.`;
    const simpleLogo = data.public_logo_url || "";
    const simpleImg = simpleLogo.startsWith("data:") ? undefined : simpleLogo || undefined;
    return (
      <div data-testid={websiteOff ? "ms-layout-inactive" : "ms-layout-simple"}>
        <SEO
          title={`${simpleName}: Rentals`}
          description={simpleDesc}
          canonical={`/rentals/${simpleSlug}`}
          og={{ image: simpleImg }}
        />
        <DemoBusinessGuard isDemo={!!data.is_demo} companyId={data.id} />
        <SimpleTopBar slug={simpleSlug} name={simpleName} description={simpleDesc} />
        {websiteOff && (
          <div className="max-w-5xl mx-auto px-4 py-3" data-testid="ms-website-inactive-banner">
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              The business has temporarily turned off their public website. Their simple
              profile remains available below.
            </div>
          </div>
        )}
        <RentalProfile preloaded={data} />
      </div>
    );
  }

  // If they landed on `/rentals/:slug` with no sub-route, send to /home.
  const isBare =
    location.pathname.replace(/\/$/, "").endsWith(`/rentals/${slug}`) ||
    location.pathname.replace(/\/$/, "").endsWith(`/rentals/${slug}/`);
  if (isBare) return <Navigate to={`/rentals/${slug}/home`} replace />;

  const theme = data.mini_site?.theme_color || "#1B4332";

  // ----- SEO -------------------------------------------------------------
  const canonicalSlug = (data.public_slug || slug).toLowerCase();
  // Canonical for the mini-site is always /rentals/:slug (no /home), even
  // when this layout has redirected the user to /home.  Subpages keep
  // their own URL.
  const subMatch = location.pathname.match(/\/rentals\/[^/]+\/([a-z]+)$/i);
  const sub = subMatch ? subMatch[1].toLowerCase() : "";
  const canonical = (sub && sub !== "home")
    ? `/rentals/${canonicalSlug}/${sub}`
    : `/rentals/${canonicalSlug}`;
  const bizName = data.name || "Rental business";
  const svcMap = {
    car_rental: "Car",
    motorbike_rental: "Motorbike / Scooter",
    motor_rental: "Motorbike / Scooter",
    scooter_rental: "Motorbike / Scooter",
    car_and_motorbike_rental: "Car & Motorbike",
    mixed_fleet: "Car & Motorbike",
  };
  const svcLabel = svcMap[(data.business_type || "").toLowerCase()] || "Car & Motorbike";
  const subTitles = {
    "": "Rentals", home: "Rentals", fleet: "Fleet", booking: "Book",
    about: "About", policies: "Policies", faq: "FAQ",
    reviews: "Reviews", contact: "Contact",
  };
  const titleLabel = subTitles[sub] || svcLabel;
  const seoTitle = (sub && sub !== "home")
    ? `${bizName}: ${titleLabel}`
    : `${bizName}: ${svcLabel} Rentals`;
  const seoDescription =
    (data.public_description_full || data.description || "").slice(0, 220) ||
    `View ${bizName}'s rental fleet, policies, booking options, and contact details through ConfirmPH.` +
    (data.city ? ` Based in ${data.city}.` : "");

  // Image priority: hero → logo → ConfirmPH brand fallback.
  // Skip data: URIs (FB crawlers can't fetch them); the SEO lib will use
  // /og-default.png automatically when og.image is falsy.
  const heroRaw = data.mini_site?.hero_image_url || "";
  const logoRaw = data.public_logo_url || "";
  let ogImage;
  if (heroRaw && !heroRaw.startsWith("data:")) ogImage = heroRaw;
  else if (logoRaw && !logoRaw.startsWith("data:")) ogImage = logoRaw;

  // LocalBusiness JSON-LD, but only expose city, never the exact street
  // address when the business chose hidden/approximate location mode.
  const locMode = (data.public_location_mode || "city").toLowerCase();
  const localBusinessLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: bizName,
    url: absUrl(`/rentals/${canonicalSlug}`),
    image: ogImage ? absUrl(ogImage) : undefined,
    areaServed: data.city || "Philippines",
  };
  if (locMode === "city" || locMode === "approximate") {
    localBusinessLd.address = {
      "@type": "PostalAddress",
      addressLocality: data.city || undefined,
      addressCountry: "PH",
    };
  } else if (locMode === "exact" && data.public_address) {
    localBusinessLd.address = {
      "@type": "PostalAddress",
      streetAddress: data.public_address,
      addressLocality: data.city || undefined,
      addressCountry: "PH",
    };
  }

  return (
    <MiniSiteContext.Provider value={{ data, slug, theme }}>
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonical={canonical}
        og={{ image: ogImage }}
        jsonLd={[localBusinessLd]}
      />
      <div className="min-h-screen bg-white" data-testid="ms-layout">
        <DemoBusinessGuard isDemo={!!data.is_demo} companyId={data.id} />
        <MiniSiteNav company={data} theme={theme} />
        <main data-testid="ms-main">
          <Outlet />
        </main>
        <MiniSiteFooter theme={theme} />
      </div>
    </MiniSiteContext.Provider>
  );
}
